import { useEffect, useState, type ReactNode } from 'react'

import { api } from '../../api/repository'
import {
  ROLE_TITLES,
  TRADE_STATUS_CODES,
  TRADE_STATUS_TITLES,
  type Collection,
  type TradeOffer,
  type UserProfile
} from '../../model/types'
import {
  Button,
  EmptyState,
  Panel,
  Progress,
  ScreenHeader,
  Section,
  SectionHeader,
  Stat,
  StatRow
} from '../../ui/primitives'
import { TextField } from '../../ui/fields'
import { useApp } from '../../state/AppState'
import { useRemote, type Remote } from '../../state/useRemote'
import { AppSettings } from './AppSettings'
import styles from './profile.module.css'

/**
 * Личный кабинет: кто я, что собрал, что происходит с обменами и что
 * можно настроить. Раскладка снята с кабинета nox-assist — блок
 * личности, сводка цифрами, дальше секции сверху вниз.
 *
 * Блоки грузятся независимо: бэкенд отвечает пока не на все методы, и
 * неответивший раздел не должен уносить с собой остальной экран.
 */
export function ProfileScreen(): ReactNode {
  const app = useApp()

  // Профиль берём из сессии, а если её ещё не восстановили после
  // перезагрузки — спрашиваем сервер.
  const remoteUser = useRemote<UserProfile>(
    () => (app.user !== null ? Promise.resolve(app.user) : api.me()),
    [app.user?.id ?? null]
  )
  const user = app.user ?? remoteUser.data

  const collections = useRemote(() => api.myCollections(), [])
  const duplicates = useRemote(() => api.duplicates(), [])
  const trades = useRemote(() => api.trades(), [])

  if (user === null) {
    return (
      <Section gap={20}>
        <ScreenHeader title="Кабинет" />
        {remoteUser.isLoading ? (
          <span className="label">Загрузка…</span>
        ) : (
          <div className={styles.failure}>
            {remoteUser.error ?? 'Профиль недоступен'}
          </div>
        )}
      </Section>
    )
  }

  return (
    <div className={styles.screen}>
      <ScreenHeader title="Кабинет" subtitle={`@${user.login}`} />

      <Identity user={user} />

      <StatRow>
        <Stat value={ratingText(user.rating)} caption="рейтинг" />
        <Stat value={String(user.tradesCount)} caption="обменов" />
        <Stat value={countText(collections)} caption="коллекций" />
        <Stat value={countText(duplicates)} caption="дублей" />
      </StatRow>

      <ProfileForm user={user} />

      <Section>
        <SectionHeader
          title="Мои коллекции"
          trailing={countText(collections)}
        />
        <Block
          remote={collections}
          empty="Вы ещё не отметили ни одной коллекции"
        >
          {(items) => (
            <Panel>
              {items.map((collection) => (
                <CollectionRow
                  key={collection.id}
                  collection={collection}
                />
              ))}
            </Panel>
          )}
        </Block>
      </Section>

      <Section>
        <SectionHeader title="Сделки" trailing={countText(trades)} />
        <Block remote={trades} empty="Обменов пока не было">
          {(items) => (
            <Panel>
              {items.map((trade) => (
                <TradeRow key={trade.id} trade={trade} />
              ))}
            </Panel>
          )}
        </Block>
      </Section>

      <AppSettings />
    </div>
  )
}

// MARK: - Личность

function Identity({ user }: { user: UserProfile }): ReactNode {
  return (
    <div className={styles.identity}>
      {user.avatarUrl !== null ? (
        <img className={styles.monogram} src={user.avatarUrl} alt="" />
      ) : (
        <span className={styles.monogram} aria-hidden="true">
          {initials(user.displayName)}
        </span>
      )}
      <div className={styles.identityText}>
        <span className={styles.name}>{user.displayName}</span>
        <div className={styles.identityMeta}>
          <span className={styles.roleTag}>{ROLE_TITLES[user.role]}</span>
          <span className="mono muted">@{user.login}</span>
          {user.city !== null && (
            <span className="muted">{user.city}</span>
          )}
        </div>
      </div>
    </div>
  )
}

// MARK: - Правка данных

function ProfileForm({ user }: { user: UserProfile }): ReactNode {
  const app = useApp()
  const [displayName, setDisplayName] = useState(user.displayName)
  const [city, setCity] = useState(user.city ?? '')
  const [isSaving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Форма перечитывается при смене пользователя, а не на каждый рендер:
  // иначе набранный текст затирало бы фоновое обновление профиля.
  useEffect(() => {
    setDisplayName(user.displayName)
    setCity(user.city ?? '')
  }, [user.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const trimmedName = displayName.trim()
  const trimmedCity = city.trim()
  const hasChanges =
    trimmedName !== user.displayName ||
    trimmedCity !== (user.city ?? '')
  const canSave = hasChanges && trimmedName.length > 0 && !isSaving

  async function save(): Promise<void> {
    setSaving(true)
    setError(null)
    try {
      const updated = await api.updateMe({
        displayName: trimmedName,
        // Пустое поле — это «город не указан», а не пустая строка:
        // подбор партнёров сравнивает города, и '' совпало бы с ''.
        city: trimmedCity.length > 0 ? trimmedCity : null
      })
      app.setUser(updated)
    } catch (failure) {
      setError(
        failure instanceof Error
          ? failure.message
          : 'Не удалось сохранить'
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Section>
      <SectionHeader
        title="Данные"
        trailing={hasChanges ? 'есть правки' : 'сохранено'}
      />
      <div className={styles.form}>
        <TextField
          label="Имя"
          value={displayName}
          onChange={setDisplayName}
          placeholder="Как вас зовут"
          autoComplete="nickname"
          onSubmit={() => {
            if (canSave) void save()
          }}
        />
        <TextField
          label="Город"
          value={city}
          onChange={setCity}
          placeholder="Москва"
          autoComplete="address-level2"
          hint="По городу ищут партнёров: обмен из рук в руки дешевле и быстрее почтового"
        />
        <Button kind="primary" disabled={!canSave} onClick={() => void save()}>
          {isSaving ? 'Сохраняем…' : 'Сохранить'}
        </Button>
        {error !== null && <div className={styles.failure}>{error}</div>}
      </div>
    </Section>
  )
}

// MARK: - Строки списков

function CollectionRow({
  collection
}: {
  collection: Collection
}): ReactNode {
  const owned = collection.ownedCount ?? 0
  return (
    <div className={styles.collection}>
      <div className={styles.collectionHead}>
        <span className={styles.collectionTitle}>{collection.title}</span>
        <span className={styles.collectionCount}>
          {owned} / {collection.itemsTotal}
        </span>
      </div>
      <Progress
        value={
          collection.itemsTotal > 0 ? owned / collection.itemsTotal : 0
        }
      />
    </div>
  )
}

function TradeRow({ trade }: { trade: TradeOffer }): ReactNode {
  return (
    <div className={styles.trade}>
      <span
        className={[
          styles.tradeCode,
          trade.status === 'completed' ? styles.tradeCodeDone : ''
        ]
          .filter(Boolean)
          .join(' ')}
        title={TRADE_STATUS_TITLES[trade.status]}
      >
        {TRADE_STATUS_CODES[trade.status]}
      </span>
      <span className={styles.tradeText}>
        <span className={styles.tradePartner}>
          {trade.partner.displayName}
        </span>
        <span className="label">
          {TRADE_STATUS_TITLES[trade.status]}
        </span>
      </span>
      <span className={styles.tradeScore}>
        {trade.give.length} ↔ {trade.take.length}
      </span>
    </div>
  )
}

// MARK: - Общая обёртка блока

/**
 * Три состояния одного блока: грузится, не ответил, пусто. Собраны в
 * одном месте, чтобы каждая секция не переписывала их заново.
 */
function Block<T>({
  remote,
  empty,
  children
}: {
  remote: Remote<T[]>
  empty: string
  children: (items: T[]) => ReactNode
}): ReactNode {
  if (remote.isLoading) return <span className="label">Загрузка…</span>
  if (remote.error !== null) {
    return <div className={styles.failure}>{remote.error}</div>
  }
  const items = remote.data ?? []
  if (items.length === 0) {
    return (
      <Panel>
        <EmptyState text={empty} />
      </Panel>
    )
  }
  return children(items)
}

// MARK: - Форматирование

/** Пока обменов не было, рейтинг равен нулю — показываем прочерк. */
function ratingText(rating: number): string {
  return rating > 0 ? rating.toFixed(1) : '—'
}

/** Счётчик показывается только когда блок ответил. */
function countText(remote: Remote<unknown[]>): string {
  return remote.data === null ? '—' : String(remote.data.length)
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '—'
  return parts
    .slice(0, 2)
    .map((part) => [...part][0] ?? '')
    .join('')
}
